import React, { useState } from 'react';
import { FileText, ExternalLink } from 'lucide-react';

/**
 * CitationBadge Component
 * Renders an inline citation as a clickable superscript badge
 */
const CitationBadge = ({
    number,
    source,
    onViewFile
}) => {
    const [showTooltip, setShowTooltip] = useState(false);

    const handleClick = () => {
        if (onViewFile && source?.file_id) {
            onViewFile(source);
        }
    };

    return (
        <span className="relative inline-block">
            <button
                onClick={handleClick}
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
                className="inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold text-primary bg-primary/10 hover:bg-primary/20 rounded-full cursor-pointer transition-colors align-super -mt-1 mx-0.5"
                aria-label={`Citation ${number}: ${source?.file_name || 'Source'}`}
            >
                {number}
            </button>

            {/* Tooltip */}
            {showTooltip && source && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 pointer-events-none">
                    <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg max-w-xs whitespace-normal">
                        <div className="flex items-center gap-2 mb-1">
                            <FileText size={12} className="text-primary-light flex-shrink-0" />
                            <span className="font-medium truncate">{source.file_name}</span>
                        </div>
                        {source.chunk_text && (
                            <p className="text-gray-300 text-[10px] line-clamp-2 leading-relaxed">
                                "{source.chunk_text}"
                            </p>
                        )}
                        {source.similarity_score && (
                            <div className="mt-1.5 pt-1.5 border-t border-gray-700 flex items-center justify-between">
                                <span className="text-gray-400">Relevance</span>
                                <span className="text-primary-light font-medium">
                                    {Math.round(source.similarity_score * 100)}%
                                </span>
                            </div>
                        )}
                        {/* Arrow */}
                        <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px">
                            <div className="border-4 border-transparent border-t-gray-900" />
                        </div>
                    </div>
                </div>
            )}
        </span>
    );
};

/**
 * AIAnswerWithCitations Component
 * Renders AI answer text with inline citation badges
 */
export const AIAnswerWithCitations = ({
    answer,
    sources = [],
    onViewFile
}) => {
    // Parse answer text and replace [1], [2], etc. with CitationBadge components
    const renderAnswerWithCitations = () => {
        if (!answer) return null;

        // Split by citation pattern [1], [2], etc.
        const parts = answer.split(/(\[\d+\])/g);

        return parts.map((part, index) => {
            // Check if this part is a citation reference
            const citationMatch = part.match(/\[(\d+)\]/);

            if (citationMatch) {
                const citationNumber = parseInt(citationMatch[1], 10);
                const source = sources[citationNumber - 1]; // Sources are 0-indexed, citations are 1-indexed

                return (
                    <CitationBadge
                        key={index}
                        number={citationNumber}
                        source={source}
                        onViewFile={onViewFile}
                    />
                );
            }

            // Regular text
            return <span key={index}>{part}</span>;
        });
    };

    return (
        <div className="ai-answer prose prose-sm max-w-none">
            <div className="text-gray-800 leading-relaxed">
                {renderAnswerWithCitations()}
            </div>
        </div>
    );
};

/**
 * SourcesList Component
 * Renders the list of source files with view buttons
 */
export const SourcesList = ({
    sources = [],
    onViewFile
}) => {
    if (sources.length === 0) return null;

    return (
        <div className="mt-4 pt-4 border-t border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <FileText size={14} className="text-gray-500" />
                Sources ({sources.length})
            </h4>
            <div className="space-y-2">
                {sources.map((source, index) => (
                    <div
                        key={source.file_id || index}
                        className="flex items-center justify-between p-2.5 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors group"
                    >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">
                                {index + 1}
                            </span>
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                    {source.file_name}
                                </p>
                                {source.similarity_score && (
                                    <p className="text-xs text-gray-500">
                                        {Math.round(source.similarity_score * 100)}% match
                                    </p>
                                )}
                            </div>
                        </div>
                        <button
                            onClick={() => onViewFile && onViewFile(source)}
                            className="flex-shrink-0 p-1.5 text-gray-400 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                            title="View file"
                        >
                            <ExternalLink size={14} />
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default CitationBadge;
